/* eslint-disable max-lines */
import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { ExportVersionFile } from 'common/types';
import '@fuxi/image-react/style.css';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import { Button, Modal, Checkbox, Flex, Typography } from 'antd';
import { SecondaryButton } from 'common/components/SecondrayButton';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import MyIcon from 'common/components/EeveeIcon';
import { useTaskEditorContext } from '@/context/TaskEditorContext';
import ImageFill from '@/assets/image/babylon-error-img.png';
import styles from './index.module.css';

const { Text, Paragraph } = Typography;
const cx = classNames.bind(styles);

const DragHandle = SortableHandle(() => (
  <span className="cursor-grab">
    <MyIcon icon="move" size="14px" />
  </span>
));

const SortableItem = SortableElement<{
  item: ExportVersionFile;
}>(({ item }) => {
  const name = item?.name || item?.fileName;
  return (
    <div className="select-none z-10000 flex gap-4 items-center">
      <DragHandle />
      <Checkbox value={item.uuid}></Checkbox>
      <div className={cx('preview-list-item')}>
        <Flex gap={12}>
          <div className={cx('thumb-wrapper')}>
            <LazyLoadImage
              width={88}
              height={66}
              alt={item.name}
              src={item.thumbnail}
              placeholderSrc={ImageFill}
              object-fit="cover"
              className={cx('preview-list-item-img')}
            />
            <span className={cx('icon')}>{name?.split('.').pop()}</span>
          </div>

          <div className={cx('preview-list-item-info')}>
            <Paragraph
              ellipsis={{
                rows: 2,
                tooltip: name,
              }}>
              {name}
            </Paragraph>
          </div>
        </Flex>
      </div>
    </div>
  );
});

const SortableList = SortableContainer<{
  items: ExportVersionFile[];
  selected?: string[];
  onChange: (items: string[]) => void;
}>(({ items, onChange, selected }) => {
  const onCheckboxChange = checkedValues => {
    onChange(checkedValues);
    setSelectedItems(checkedValues);
  };
  const [selectedItems, setSelectedItems] = useState<string[]>(selected || []);
  useEffect(() => {
    setSelectedItems(selected || []);
  }, [selected]);

  return (
    <div className="pt-3 overflow-auto max-h-520px">
      <Checkbox.Group value={selectedItems} style={{ width: '100%' }} onChange={onCheckboxChange}>
        {items.map((value, index) => (
          <SortableItem key={value.uuid} index={index} item={value} />
        ))}
      </Checkbox.Group>
    </div>
  );
});

const VideoCard = ({ item, playing }) => {
  const name = item?.name || item?.fileName;
  return (
    <div className={cx(styles['video-card'], 'w-124px', 'h-96px', 'relative')}>
      <LazyLoadImage
        width={124}
        height={96}
        alt={item.name}
        src={item.thumbnail}
        placeholderSrc={ImageFill}
        object-fit="cover"
        className={cx('preview-list-item-img')}
      />
      <div
        className={cx(styles.mask, 'absolute', 'inset-0', 'flex', 'items-center', 'justify-center', {
          playing: playing,
        })}>
        <div className={cx(styles.info)}>
          <Paragraph
            ellipsis={{
              rows: 2,
              tooltip: name,
            }}>
            {name}
          </Paragraph>
        </div>
      </div>
    </div>
  );
};
type Props = {
  editModalVisible?: boolean;
  onChange?: (playList: ExportVersionFile[]) => void;
  onSelect?: (file: ExportVersionFile) => void;
  onIndexChange: (index: number) => void;
  currentPlayIndex: number;
};

const PlayList: React.FC<Props> = ({ onChange, editModalVisible, onIndexChange }) => {
  const {
    mode,
    exportFile,
    previewType,

    fileLoading,
    workflow,
    modelViewerHide,
    onModelViewerSnapShot,
    onPaletteEnable,
    setIsModelViewerError,
    playerCtrRef,
    isPlayerPause,
    setIsPlayerPause,

    setPlayerTransform,
    isAnnotationShow,
    setIsAnnotationShow,
    fileList,
    restorePlayer,
    currentIndex,
    cachedPlayerInfo,
    modelInspectShow,
    setModelInspectShow,
    isPlayList,
    setIsPlayList,
    versionId,
    currentPlayIndex,
    setCurrentPlayIndex,
    onPlayIndexChange,
  } = useTaskEditorContext();

  const [isModalOpen, setIsModalOpen] = useState(editModalVisible);

  const [items, setItems] = useState<ExportVersionFile[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [playListFiles, setPlayListFiles] = useState<ExportVersionFile[]>([]);
  // const [playIndex, setPlayIndex] = useState(0);
  // const [playListConfig, setPlayListConfig] = useLocalStorageState('')

  // useEffect(() => {
  //   setPlayIndex(currentPlayIndex);
  // }, [currentPlayIndex]);

  const initPlayList = useCallback(() => {
    const playListConfigString = window.localStorage.getItem('playlist-' + versionId);
    if (!playListConfigString) {
      return false;
    }
    const playListConfig = JSON.parse(playListConfigString);
    const selectedItems = playListConfig.map(item => items.find(i => i.uuid === item.uuid));
    setPlayListFiles(selectedItems);
    return true;
  }, [items]);

  useEffect(() => {
    setItems(fileList.filter(item => item.type === 'Video'));
  }, [fileList]);

  useEffect(() => {
    if (isPlayList && !playListFiles.length) {
      setSelectedItems(items.map(item => item.uuid));
      setIsModalOpen(true);
    }
  }, [isPlayList, items]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    const playList = items.filter(item => selectedItems.includes(item.uuid));
    setPlayListFiles(playList);
    onChange?.(playList);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    if (!playListFiles.length) {
      setIsPlayList(false);
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setItems(arrayMoveImmutable(items, oldIndex, newIndex));
  };

  useEffect(() => {
    setIsModalOpen(editModalVisible);
  }, [editModalVisible]);

  const onListChange = (items: string[]) => {
    setSelectedItems(items);
  };

  const selectAll = () => {
    if (selectedItems.length == items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.uuid));
    }
  };
  // const playListFiles = useMemo(() => {
  //   return items.filter(item => selectedItems.includes(item.uuid));
  // }, [items, selectedItems]);

  return (
    <div className={cx('play-list', !isPlayList && 'hidden')}>
      {isPlayList && (
        <div className="flex gap-3">
          <div
            onClick={() => {
              setIsModalOpen(true);
            }}
            className="p-x-1 w-14px flex justify-center items-center flex-col gap-1 h-96px text-color-primary cursor-pointer">
            <MyIcon icon="settingfilled" size={14}></MyIcon>
            <div className="text-size-12px text-wrap">播放设置</div>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            {playListFiles.map((item, index) => {
              return (
                <div
                  onClick={() => {
                    setCurrentPlayIndex(index);
                    onPlayIndexChange();
                    onIndexChange(index);
                  }}>
                  <VideoCard item={item} playing={index == currentPlayIndex}></VideoCard>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <Modal
        title="播放设置"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={388}
        cancelButtonProps={{
          type: 'default',
        }}
        styles={{
          body: {
            maxHeight: '618px',
            padding: '20px 0 0',
          },
        }}
        footer={
          <>
            <SecondaryButton onClick={handleCancel}>取消</SecondaryButton>
            <Button type={'primary'} disabled={!selectedItems.length} onClick={handleOk}>
              确定
            </Button>
          </>
        }>
        <div>
          <div>请选择要加入连播队列的视频文件：</div>
          <div className="flex justify-between mt-3">
            <Checkbox checked={selectedItems.length == items.length} onChange={selectAll}>
              全选
            </Checkbox>
            <div className="text-color-tertiary">
              <span>已选</span>
              <span>{selectedItems.length}</span>
              <span>个</span>
            </div>
          </div>
        </div>
        <SortableList
          items={items}
          onSortEnd={onSortEnd}
          selected={selectedItems}
          useDragHandle
          lockAxis="y"
          onChange={onListChange}></SortableList>
      </Modal>
    </div>
  );
};
export default React.memo(PlayList);
